// Koppelingen · Maildomeinen (KoppelingenDomeinen, KoppelingenDomein): een supportgesprek telt voor de klant van het
// afzenderdomein. Bovenaan de onbekende domeinen (met een voorstel als het domein de klantnaam bevat), dan wat
// gekoppeld of genegeerd is, en de klanten zonder domein. Zelfde API en lade als Support (api-support,
// DomainLinkDrawer); een keuze geldt ook voor gesprekken die al binnen zijn. Met naam en tijd in het logboek.
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { whenText } from "../lib/api-teams";
import { supportApi, useDomainLinks, type DomainLinkRow } from "../lib/api-support";
import { suggestKlant } from "../lib/support";
import type { Klant } from "../lib/types";
import { LoadError } from "../shell/Layout";
import { useToast } from "../shell/Toast";
import { DomainLinkDrawer } from "./DomainLinkDrawer";
import { KoppelCard, KoppelList, OFF, OK, WARN, type KRow } from "./KoppelList";
import { KoppelPicker, type PickOpt } from "./KoppelPicker";

const IGNORE = "__geen";
const nOf = (x: number, one: string, many: string) => x + " " + (x === 1 ? one : many);
/** "jan@klant.be", "https://www.klant.be/" en "@klant.be" worden klant.be */
const cleanDomain = (s: string) => s.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^.*@/, "").replace(/\/.*$/, "").replace(/^www\./, "");
const DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;

type Pick = { kind: "domain"; d: DomainLinkRow } | { kind: "new"; k: Klant };

export function KoppelDomeinen({ q, klanten, mailbox, today }: { q: string; klanten: Klant[]; mailbox: string; today: string }) {
  const dq = useDomainLinks();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<string | null>(null); // onbekend domein: de lade van Support
  const [pick, setPick] = useState<Pick | null>(null);
  const [newDom, setNewDom] = useState("");
  const kName = (pid: string | null) => klanten.find((k) => k.id === pid)?.name || "";

  // ---------- schrijven, met Ongedaan maken ----------
  const refresh = () => { for (const k of ["domain-links", "support", "audit"]) qc.invalidateQueries({ queryKey: [k] }); };
  const run = async (doIt: () => Promise<unknown>, text: string, undo: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await doIt();
      refresh();
      toast.flash(text, {
        undo: () => { undo().then(() => { refresh(); toast.flash("Ongedaan gemaakt"); }, (e: Error) => toast.flash(e.message, { error: true })); },
      });
      return true;
    } catch (e) {
      toast.flash((e as Error).message, { error: true });
      return false;
    } finally {
      setBusy(false);
    }
  };
  /** prev: de koppeling van daarvoor; zonder was het domein onbekend */
  const setLink = (domain: string, pid: string | null, prev?: DomainLinkRow) => run(
    () => supportApi.link(domain, pid),
    pid ? domain + " gekoppeld aan klant " + kName(pid) : domain + " genegeerd: telt niet mee per klant",
    () => (prev ? supportApi.link(domain, prev.projectId) : supportApi.unlink(domain)),
  );
  const unlink = (l: DomainLinkRow) => run(() => supportApi.unlink(l.domain), l.domain + " ontkoppeld: weer een onbekend domein", () => supportApi.link(l.domain, l.projectId));

  const title = "Afzenderdomeinen en klanten", sub = "Een supportgesprek telt voor de klant van het afzenderdomein · " + mailbox + ", alleen metadata";
  const foot = "De app bewaart elke koppeling zelf; een keuze geldt ook voor gesprekken die al binnen zijn.";
  if (!dq.data) {
    return (
      <KoppelCard title={title} sub={sub} foot={foot}>
        <div className="p-kp-list">{dq.error ? <LoadError what="De maildomeinen" onRetry={() => dq.refetch()} /> : <div className="p-loading">Maildomeinen laden…</div>}</div>
      </KoppelCard>
    );
  }

  const { links, unknown } = dq.data;
  const qq = q.trim().toLowerCase();
  const m = (...s: string[]) => !qq || s.join(" ").toLowerCase().includes(qq);
  const convs = (n: number) => nOf(n, "gesprek", "gesprekken");
  const rows: KRow[] = [];
  for (const u of unknown) {
    const sug = suggestKlant(u.domain, klanten);
    if (!m(u.domain, sug?.name || "")) continue;
    const base = { key: "u:" + u.domain, a: u.domain, aSub: convs(u.conversations) + " · nog geen klant" };
    if (sug) {
      rows.push({
        ...base, b: sug.name, bSub: "voorstel op naam: het domein bevat de klantnaam", pill: "Voorstel", pillCls: WARN,
        acts: [{ label: "Bevestigen", primary: true, run: () => setLink(u.domain, sug.id) }, { label: "Andere klant", run: () => setDrawer(u.domain) }],
      });
    } else {
      rows.push({
        ...base, b: "Geen klant", bSub: "platform of leverancier: negeren; anders een klant kiezen", pill: "Nieuw", pillCls: WARN,
        acts: [{ label: "Negeren", primary: true, title: "Telt niet mee per klant", run: () => setLink(u.domain, null) }, { label: "Klant kiezen", run: () => setDrawer(u.domain) }],
      });
    }
  }
  const done = links.filter((l) => m(l.domain, l.klant || "")).sort((a, b) => b.conversations - a.conversations || a.domain.localeCompare(b.domain));
  if (done.length) rows.push({ key: "h:done", head: "Gekoppeld of genegeerd" });
  for (const l of done) {
    const ign = !l.projectId;
    rows.push({
      key: "l:" + l.domain, a: l.domain, aSub: convs(l.conversations) + " · sinds " + whenText(l.createdAt, today),
      b: ign ? "Geen klant" : l.klant || "Onbekend klantplan", bSub: ign ? "telt niet mee per klant" : l.klant ? "afzenders van dit domein tellen voor " + l.klant : "het klantplan bestaat niet meer",
      bSubCls: !ign && !l.klant ? "t-warn" : "", pill: ign ? "Genegeerd" : "Gekoppeld", pillCls: ign ? OFF : OK,
      acts: [{ label: "Wijzigen", run: () => setPick({ kind: "domain", d: l }) }, { label: "Ontkoppelen", title: "Weer een onbekend domein", run: () => unlink(l) }],
    });
  }
  const has = new Set(links.map((l) => l.projectId).filter(Boolean));
  const without = klanten.filter((k) => !has.has(k.id) && m(k.name)).sort((a, b) => a.name.localeCompare(b.name));
  if (without.length) rows.push({ key: "h:leeg", head: "Klanten zonder maildomein" });
  for (const k of without) {
    rows.push({
      key: "k:" + k.id, a: "Nog geen domein", aSub: "geen afzenderdomein gekoppeld", b: k.name, bSub: "support van deze klant wordt nog niet herkend", pill: "Leeg", pillCls: OFF,
      acts: [{ label: "Domein toevoegen", primary: true, run: () => { setNewDom(""); setPick({ kind: "new", k }); } }],
    });
  }

  // ---------- lade: een gekoppeld domein wijzigen, of een domein toevoegen voor een klant ----------
  let lade = null;
  if (pick?.kind === "domain") {
    const d = pick.d, sug = suggestKlant(d.domain, klanten), cur = d.projectId || IGNORE;
    const opts: PickOpt[] = [...klanten].sort((a, b) => a.name.localeCompare(b.name)).map((k) => ({
      key: k.id, label: k.name, note: k.id === d.projectId ? "nu gekoppeld" : k.id === sug?.id ? "voorstel op naam" : undefined,
      pill: k.id === sug?.id ? "Voorstel" : undefined, pillCls: OK,
    }));
    opts.push({ key: IGNORE, label: "Geen klant", note: "platform of leverancier; telt niet mee per klant", keep: true });
    lade = (
      <KoppelPicker key={d.domain} eyebrow="Maildomein koppelen" title={d.domain}
        state={d.projectId ? ["Hoort bij " + (d.klant || "een klantplan dat niet meer bestaat"), OK] : ["Genegeerd", OFF]}
        kv={[["Gesprekken", convs(d.conversations)], ["Gekoppeld sinds", whenText(d.createdAt, today)]]}
        listTitle="Kies de klant" options={opts} initial={cur}
        note="De keuze geldt ook voor gesprekken die al binnen zijn: de telling per klant rekent opnieuw."
        confirmText={() => "Opslaan"} busy={busy} onClose={() => setPick(null)}
        onConfirm={(sel) => {
          if (!sel || sel === cur) { setPick(null); return; }
          setLink(d.domain, sel === IGNORE ? null : sel, d).then((ok) => { if (ok) setPick(null); });
        }} />
    );
  } else if (pick?.kind === "new") {
    const k = pick.k;
    const add = () => {
      const d = cleanDomain(newDom);
      if (!DOMAIN.test(d)) { toast.flash("Vul een domein in, bijvoorbeeld " + (k.name.toLowerCase().replace(/[^a-z0-9]/g, "") || "klant") + ".be", { error: true }); return; }
      const there = links.find((l) => l.domain === d);
      if (there) { toast.flash(d + " staat er al: " + (there.klant ? "gekoppeld aan " + there.klant : "genegeerd"), { error: true }); return; }
      setLink(d, k.id).then((ok) => { if (ok) setPick(null); });
    };
    lade = (
      <KoppelPicker key={"new:" + k.id} eyebrow="Maildomein toevoegen" title={k.name} state={["Nog geen domein", OFF]}
        kv={[["Klant", k.name], ["Klantplan", k.plan]]} listTitle="Afzenderdomein" options={[]} initial={null} ready={newDom.trim() !== ""}
        note={"Mails van dit domein en zijn subdomeinen tellen daarna voor " + k.name + ", ook wat al binnen is."}
        confirmText={() => "Toevoegen"} busy={busy} onConfirm={add} onClose={() => setPick(null)}>
        <input className="ex-input p-kp-in" type="text" placeholder="bijvoorbeeld klant.be" aria-label="Afzenderdomein" autoFocus value={newDom}
          onChange={(e) => setNewDom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
      </KoppelPicker>
    );
  }

  return (
    <>
      <KoppelCard title={title} sub={sub} foot={foot}>
        <KoppelList rows={rows} busy={busy} empty={qq ? "Niets gevonden voor " + q.trim() + "." : "Nog geen afzenderdomeinen: de supportmailbox is nog niet gelezen."} />
      </KoppelCard>
      {lade}
      {drawer && (
        <DomainLinkDrawer start={drawer} klanten={klanten} onClose={() => setDrawer(null)}
          unknown={unknown.map((u) => ({ ...u, open: 0, lastReceived: "" }))} />
      )}
    </>
  );
}
